import * as React from 'react';
import * as Sentry from '@sentry/react';
import { Box, Typography } from '@mui/material';
import { TriangleAlert } from 'lucide-react';
import { HAIRLINE } from '../colors';
import { TimelineEntry } from '../../../../types/customers';
import useCashDiscount, {
  GetAllInPrice,
} from '../../../../hooks/payments/useCashDiscount';
import {
  describeTimelineEntry,
  TimelineEntryContent,
} from '../../../../helpers/customers/timelineEntryContent';
import {
  RAIL_COLUMN_GAP,
  RAIL_NODE_WIDTH,
} from '../../../../constants/customers';

interface Props {
  entry: TimelineEntry;
  isLast: boolean;
  timezone: string;
}

const FALLBACK_CONTENT: TimelineEntryContent = {
  Icon: TriangleAlert,
  meta: '',
  title: 'Unable to display this activity',
  body: null,
  badge: null,
};

function describeEntrySafely(
  entry: TimelineEntry,
  timezone: string,
  getAllInPrice: GetAllInPrice,
): { content: TimelineEntryContent; renderError: unknown } {
  try {
    return {
      content: describeTimelineEntry(entry, timezone, getAllInPrice),
      renderError: null,
    };
  } catch (renderError) {
    return { content: FALLBACK_CONTENT, renderError };
  }
}

function TimelineEntryRow({ entry, isLast, timezone }: Props) {
  const { getAllInPrice } = useCashDiscount();
  const { content, renderError } = React.useMemo(
    () => describeEntrySafely(entry, timezone, getAllInPrice),
    [entry, timezone, getAllInPrice],
  );

  React.useEffect(() => {
    if (!renderError) return;
    Sentry.withScope(scope => {
      scope.setTag('feature', 'customer_timeline');
      scope.setContext('timeline_entry', {
        entryId: entry.id,
        entryType: entry.type,
      });
      Sentry.captureException(renderError);
    });
  }, [renderError, entry.id, entry.type]);

  const { Icon } = content;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `${RAIL_NODE_WIDTH}px 1fr`,
        columnGap: `${RAIL_COLUMN_GAP}px`,
      }}>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: `${RAIL_NODE_WIDTH}px`,
            height: `${RAIL_NODE_WIDTH}px`,
            borderRadius: '50%',
            border: '1px solid',
            borderColor: 'common.black',
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
          <Icon size={15} strokeWidth={2} color="currentColor" />
        </Box>
        {!isLast && (
          <Box sx={{ flex: 1, width: '1px', backgroundColor: HAIRLINE }} />
        )}
      </Box>
      <Box sx={{ padding: isLast ? '4px 0 8px' : '4px 0 40px' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {content.meta}
        </Typography>
        <Box
          sx={{
            color: 'text.primary',
            marginTop: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            typography: 'h3',
            fontWeight: 700,
          }}>
          {content.title}
          {content.badge && (
            <Typography
              variant="caption"
              fontWeight={600}
              component="span"
              sx={{ color: 'primary.main' }}>
              {content.badge}
            </Typography>
          )}
        </Box>
        {content.body && (
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', marginTop: '8px', lineHeight: 1.6 }}>
            {content.body}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default React.memo(TimelineEntryRow);
